import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AudioWaveform,
  Check,
  ClipboardCheck,
  Copy,
  Loader2,
  MessageSquareText,
  PanelLeftOpen,
  RefreshCw,
  ShieldAlert,
  Smile,
  Sparkles,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { useHasPermission } from "../../context/AuthContext";
import { cn } from "../../lib/utils";
import { callAnalyticsApi, errorText } from "./api";
import { ConfirmDialog } from "./confirm-dialog";
import {
  DASH,
  FEATURE_LABELS,
  formatClock,
  formatDateTime,
  formatNumber,
  formatSigned,
  humanize,
  levelTone,
  runState,
  sentimentTone,
} from "./format";
import { AcousticPanel } from "./panels/acoustic-panel";
import { CxPanel } from "./panels/cx-panel";
import { EmotionPanel } from "./panels/emotion-panel";
import { ObjectionPanel } from "./panels/objection-panel";
import { QaPanel } from "./panels/qa-panel";
import { SentimentPanel } from "./panels/sentiment-panel";
import { FeatureFrame, makeTurnRef } from "./panels/shared";
import { TabPanel } from "./tabs";
import { Transcript } from "./transcript";
import type { CallDetail as CallDetailData, FeatureKey } from "./types";
import { FEATURE_KEYS } from "./types";

type DetailTab = "transcript" | FeatureKey;

const TAB_CONFIG: Record<DetailTab, { label: string; icon: LucideIcon }> = {
  transcript: { label: "Transcript", icon: MessageSquareText },
  acoustic: { label: "Tone & acoustics", icon: AudioWaveform },
  emotion: { label: "Emotion", icon: Smile },
  sentiment: { label: "Sentiment", icon: TrendingUp },
  qa: { label: "Quality (QA)", icon: ClipboardCheck },
  objection: { label: "Objections & compliance", icon: ShieldAlert },
  cx: { label: "Customer experience", icon: Sparkles },
};

export function FeaturePanel({ feature, detail }: { feature: FeatureKey; detail: CallDetailData }) {
  const turnRef = makeTurnRef(detail.turns);
  const title = FEATURE_LABELS[feature];
  const f = detail.features;
  switch (feature) {
    case "acoustic":
      return <FeatureFrame title={title} outcome={f.acoustic}>{(r) => <AcousticPanel result={r} turnRef={turnRef} />}</FeatureFrame>;
    case "emotion":
      return <FeatureFrame title={title} outcome={f.emotion}>{(r) => <EmotionPanel result={r} />}</FeatureFrame>;
    case "sentiment":
      return <FeatureFrame title={title} outcome={f.sentiment}>{(r) => <SentimentPanel result={r} turnRef={turnRef} />}</FeatureFrame>;
    case "qa":
      return <FeatureFrame title={title} outcome={f.qa}>{(r) => <QaPanel result={r} turnRef={turnRef} />}</FeatureFrame>;
    case "objection":
      return <FeatureFrame title={title} outcome={f.objection}>{(r) => <ObjectionPanel result={r} turnRef={turnRef} />}</FeatureFrame>;
    case "cx":
      return <FeatureFrame title={title} outcome={f.cx}>{(r) => <CxPanel result={r} turnRef={turnRef} />}</FeatureFrame>;
  }
}

const DOT: Record<string, string> = { completed: "bg-success", skipped: "bg-warning", failed: "bg-danger", not_run: "bg-line" };

function ExecutiveSummaryHeader({ detail }: { detail: CallDetailData }) {
  const f = detail.features;
  const qa = f.qa?.result;
  const sentiment = f.sentiment?.result;
  const cx = f.cx?.result;
  const acoustic = f.acoustic?.result;
  const objection = f.objection?.result;
  const emotion = f.emotion?.result;

  const hasAnyAnalysis = detail.call.analysed || qa || sentiment || cx || acoustic || objection || emotion;
  if (!hasAnyAnalysis) return null;

  const dominantEmotion =
    emotion?.text?.customer_dominant ||
    emotion?.audio?.customer_dominant ||
    emotion?.text?.dominant ||
    emotion?.audio?.dominant;
  const objectionCount = objection?.objections?.length;

  return (
    <div className="grid grid-cols-2 gap-2 rounded-xl border border-line/70 bg-soft/40 p-2 sm:grid-cols-3 lg:grid-cols-6" aria-label="Executive call summary">
      {/* QA Metric */}
      <div className="flex flex-col justify-between rounded-lg border border-line/50 bg-elevated px-2.5 py-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-3">QA Score</span>
        <div className="mt-1 flex items-baseline justify-between gap-1">
          <span className="text-base font-bold text-ink">{qa?.score != null ? formatNumber(qa.score) : DASH}</span>
          {qa?.passed != null && (
            <Badge tone={qa.passed ? "success" : "danger"} className="text-[10px] px-1.5 py-0">
              {qa.passed ? "Pass" : "Fail"}
            </Badge>
          )}
        </div>
      </div>

      {/* Sentiment Metric */}
      <div className="flex flex-col justify-between rounded-lg border border-line/50 bg-elevated px-2.5 py-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-3">Sentiment</span>
        <div className="mt-1 flex items-baseline justify-between gap-1">
          <span className="text-base font-bold text-ink">{sentiment?.overall?.score != null ? formatSigned(sentiment.overall.score) : DASH}</span>
          {sentiment?.overall?.label && (
            <Badge tone={sentimentTone(sentiment.overall.label)} className="text-[10px] px-1.5 py-0 capitalize">
              {sentiment.overall.label}
            </Badge>
          )}
        </div>
      </div>

      {/* CX Metric */}
      <div className="flex flex-col justify-between rounded-lg border border-line/50 bg-elevated px-2.5 py-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-3">CX Score</span>
        <div className="mt-1 flex items-baseline justify-between gap-1">
          <span className="text-base font-bold text-ink">{cx?.cx_score != null ? formatNumber(cx.cx_score) : DASH}</span>
          {cx?.escalation_risk?.level && (
            <Badge tone={levelTone(cx.escalation_risk.level)} className="text-[10px] px-1.5 py-0 capitalize">
              {humanize(cx.escalation_risk.level)}
            </Badge>
          )}
        </div>
      </div>

      {/* Delivery / Acoustic */}
      <div className="flex flex-col justify-between rounded-lg border border-line/50 bg-elevated px-2.5 py-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-3">Delivery Quality</span>
        <div className="mt-1 flex items-baseline justify-between gap-1">
          <span className="text-base font-bold text-ink">{acoustic?.quality_score != null ? `${formatNumber(acoustic.quality_score)}` : DASH}</span>
          {acoustic?.speakers?.agent?.speaking_rate_wpm != null && (
            <span className="text-[10px] text-ink-3 tabular-nums">
              {acoustic.speakers.agent.speaking_rate_wpm} wpm
            </span>
          )}
        </div>
      </div>

      {/* Objections */}
      <div className="flex flex-col justify-between rounded-lg border border-line/50 bg-elevated px-2.5 py-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-3">Objections</span>
        <div className="mt-1 flex items-baseline justify-between gap-1">
          <span className="text-base font-bold text-ink">{objectionCount != null ? objectionCount : DASH}</span>
          {objectionCount != null && objectionCount > 0 && (
            <Badge tone="warning" className="text-[10px] px-1.5 py-0">
              {objectionCount === 1 ? "1 item" : `${objectionCount} items`}
            </Badge>
          )}
        </div>
      </div>

      {/* Dominant Emotion */}
      <div className="flex flex-col justify-between rounded-lg border border-line/50 bg-elevated px-2.5 py-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-3">Dominant Emotion</span>
        <div className="mt-1 flex items-baseline justify-between gap-1">
          <span className="truncate text-sm font-bold text-ink capitalize" title={dominantEmotion || undefined}>
            {dominantEmotion ? humanize(dominantEmotion) : DASH}
          </span>
          {dominantEmotion && (
            <Badge tone="violet" className="text-[10px] px-1.5 py-0">
              Affect
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}

const tabId = (prefix: string, key: string) => `${prefix}-tab-${key}`;
const panelId = (prefix: string, key: string) => `${prefix}-panel-${key}`;

function ModernTabPills({
  tabs,
  value,
  onChange,
  idPrefix,
  features,
}: {
  tabs: DetailTab[];
  value: DetailTab;
  onChange: (tab: DetailTab) => void;
  idPrefix: string;
  features?: CallDetailData["features"];
}) {
  return (
    <div className="w-full">
      <div
        role="tablist"
        aria-label="Call analysis sections"
        className="flex items-center gap-1.5 overflow-x-auto pb-1 pt-0.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        {tabs.map((tabKey) => {
          const selected = tabKey === value;
          const { label, icon: Icon } = TAB_CONFIG[tabKey];
          const state = tabKey !== "transcript" && features ? runState(features[tabKey]).kind : null;

          return (
            <button
              key={tabKey}
              type="button"
              role="tab"
              id={tabId(idPrefix, tabKey)}
              aria-selected={selected}
              aria-controls={panelId(idPrefix, tabKey)}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(tabKey)}
              className={cn(
                "group inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand",
                selected
                  ? "bg-brand/10 text-brand font-semibold shadow-xs border border-brand/20 dark:bg-brand/20 dark:border-brand/40"
                  : "bg-elevated text-ink-3 border border-line hover:bg-soft hover:text-ink hover:border-line/80"
              )}
            >
              <Icon className={cn("h-3.5 w-3.5 shrink-0 transition-colors", selected ? "text-brand" : "text-ink-3 group-hover:text-ink")} aria-hidden />
              <span>{label}</span>
              {state && (
                <span
                  aria-hidden
                  className={cn(
                    "h-1.5 w-1.5 rounded-full shrink-0",
                    DOT[state] || "bg-line"
                  )}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function CallDetail({
  callId,
  isFeedCollapsed,
  onExpandFeed,
  feedCallsCount,
}: {
  callId: string;
  isFeedCollapsed?: boolean;
  onExpandFeed?: () => void;
  feedCallsCount?: number;
}) {
  const canConfigure = useHasPermission("call_analytics.configure");
  const [tab, setTab] = useState<DetailTab>("transcript");
  const [copied, setCopied] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [queueing, setQueueing] = useState(false);
  const [queued, setQueued] = useState(false);
  const [queueError, setQueueError] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ["call-analytics", "call", callId],
    queryFn: () => callAnalyticsApi.getCall(callId),
  });
  const detail = query.data;

  const handleCopyId = () => {
    if (detail?.call?.call_id) {
      navigator.clipboard.writeText(detail.call.call_id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const detailTabs: DetailTab[] = ["transcript", ...FEATURE_KEYS];

  // Explicit, confirmed, and only for configure holders: each run is billed per call.
  const analyse = async () => {
    setQueueing(true);
    setQueueError(null);
    try {
      await callAnalyticsApi.analyseCall(callId);
      setQueued(true);
      setConfirming(false);
    } catch (error) {
      setQueueError(errorText(error, "The call could not be queued."));
    } finally {
      setQueueing(false);
    }
  };

  if (query.isLoading) {
    return <div className="flex h-48 items-center justify-center" role="status" aria-label="Loading call"><Loader2 className="animate-spin text-brand" /></div>;
  }
  if (query.isError || !detail) {
    return (
      <div role="alert" className="rounded-xl border border-danger/30 bg-danger-soft p-4 text-sm text-danger">
        {errorText(query.error, "This call could not be loaded.")}
      </div>
    );
  }

  const { call } = detail;
  const idPrefix = `ca-${callId}`;

  return (
    <div className="space-y-3.5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            {isFeedCollapsed && onExpandFeed && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onExpandFeed();
                }}
                title={feedCallsCount != null ? `Show call list (${feedCallsCount})` : "Show call list"}
                aria-label="Show call list"
                className="flex h-7 w-7 items-center justify-center rounded-lg border border-line bg-elevated text-ink-3 hover:bg-soft hover:text-ink hover:border-brand/40 transition-colors shadow-xs shrink-0 animate-in fade-in"
              >
                <PanelLeftOpen className="h-4 w-4" />
              </button>
            )}
            <h2 className="text-lg font-semibold text-ink">{call.agent_name || call.agent_id || "Unknown agent"}</h2>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-ink-3">
            <span>{formatDateTime(call.call_at)}</span>
            <span>·</span>
            <span className="font-medium text-ink-2">{formatClock(call.duration_s)}</span>
            <span>·</span>
            <button
              type="button"
              onClick={handleCopyId}
              title="Copy Call ID"
              className="group inline-flex items-center gap-1 rounded bg-soft px-1.5 py-0.5 font-mono text-[11px] text-ink-2 hover:bg-line transition-colors"
            >
              <span>{call.call_id}</span>
              {copied ? (
                <Check className="h-3 w-3 text-success" />
              ) : (
                <Copy className="h-3 w-3 opacity-60 group-hover:opacity-100" />
              )}
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => query.refetch()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-line px-2.5 py-1 text-xs font-medium text-ink hover:bg-soft transition-colors"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${query.isFetching ? "animate-spin" : ""}`} aria-hidden /> Refresh
          </button>
          {canConfigure ? (
            queued ? (
              <Badge tone="brand">Queued for analysis</Badge>
            ) : (
              <button
                type="button"
                onClick={() => setConfirming(true)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-hover shadow-xs transition-colors"
              >
                <Sparkles className="h-3.5 w-3.5" aria-hidden /> Analyse this call
              </button>
            )
          ) : null}
        </div>
      </div>
      {!call.analysed && !queued ? (
        <p role="status" className="rounded-lg bg-soft px-3 py-2 text-sm text-ink-2">
          This call has not been analysed: it was not sampled, or no feature was enabled for its agent.
        </p>
      ) : null}
      {queued ? (
        <p role="status" className="text-xs text-ink-3">Queued. Results replace these when the analysis finishes; use Refresh to check.</p>
      ) : null}

      {/* Executive Call Summary Header */}
      <ExecutiveSummaryHeader detail={detail} />

      {/* Modern Icon Pills Navigation for 7 sub-views */}
      <ModernTabPills
        tabs={detailTabs}
        value={tab}
        onChange={setTab}
        idPrefix={idPrefix}
        features={detail.features}
      />

      <TabPanel idPrefix={idPrefix} tab={tab}>
        {tab === "transcript" ? <Transcript turns={detail.turns} features={detail.features} /> : <FeaturePanel feature={tab} detail={detail} />}
      </TabPanel>

      {confirming ? (
        <ConfirmDialog
          title="Analyse this call?"
          confirmLabel="Analyse"
          busy={queueing}
          onConfirm={analyse}
          onClose={() => { setConfirming(false); setQueueError(null); }}
        >
          <p>Every feature enabled for this agent runs again on this call, ignoring sampling. Each analysed call is billed.</p>
          {queueError ? <p role="alert" className="text-danger">{queueError}</p> : null}
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
